import { Exercise } from "./../../../models/Exercise";
import { addDays, format, startOfWeek } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import useApiRequest from "../../../../utils/hooks/useApiRequest";
import useFacility from "../../../../utils/hooks/useFacility";
import { GET_SCHEDULE_EXERCISES_V2 } from "../../../api/apiRoutes";
import { useSelector } from "react-redux";
import { State } from "../../../store";
import { ExerciseScheduleList } from "../../../models/ExerciseScheduleList";
import { ExerciseFiltersObject } from "./useAdditionalHooks";
import getId from "../../../../utils/getId";

export class Hours {
  constructor(data: any) {
    this.time = data.time;
    this.days = data.days;
  }
  time: string;
  days: Days[];
}

export class Days {
  constructor(data: any) {
    this.day = data.day;
    this.exercise = data.exercise;
  }
  day: string;
  exercise: Exercise[];
}

export default function useSchedule() {
  const facilityIds = useFacility();
  const { get } = useApiRequest();
  const userDataId = useSelector((state: State) => state.auth.userData.objectId);
  const today = new Date();

  // Stan dla widoku tygodniowego
  const [weekRange, setWeekRange] = useState<{ from: Date; to: Date }>({
    from: startOfWeek(new Date(), { weekStartsOn: 1 }),
    to: addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), 6),
  });

  // Stan dla widoku mobilnego (dniowego)
  const [mobileRange, setMobileRange] = useState<{ from: Date; to: Date }>({
    from: new Date(),
    to: new Date(), // mobilnie jeden dzień – można to doprecyzować
  });

  const [exercisesList, setExercisesList] = useState<ExerciseScheduleList>({
    Poniedziałek: [],
    Wtorek: [],
    Środa: [],
    Czwartek: [],
    Piątek: [],
    Sobota: [],
    Niedziela: [],
  });
  const [filters, setFilters] = useState<ExerciseFiltersObject>({} as ExerciseFiltersObject);
  const [loading, setLoading] = useState(false);

  const fetchSchedule = useCallback(
    async (from: Date, to: Date, disableLoading?: boolean) => {
      const formattedFrom = format(from, "yyyy-MM-dd");
      const formattedTo = format(to, "yyyy-MM-dd");

      if (!disableLoading) {
        setLoading(true);
      }
      const { result, isError } = await get(
        GET_SCHEDULE_EXERCISES_V2(
          facilityIds,
          formattedFrom,
          formattedTo,
          getId(filters.exerciseGroups),
          getId(filters.zones),
          getId(filters.humans),
          userDataId
        )
      );
      if (!isError && result) {
        const updatedSchedule: ExerciseScheduleList = {
          Poniedziałek: result["Poniedziałek"] || [],
          Wtorek: result["Wtorek"] || [],
          Środa: result["Środa"] || [],
          Czwartek: result["Czwartek"] || [],
          Piątek: result["Piątek"] || [],
          Sobota: result["Sobota"] || [],
          Niedziela: result["Niedziela"] || [],
        };
        setExercisesList(updatedSchedule);
      }
      setLoading(false);
    },
    [facilityIds, filters, get, userDataId]
  );

  // Sterowanie zakresem tygodniowym
  const nextWeek = useCallback(() => {
    setWeekRange((prev) => {
      const newFrom = addDays(prev.from, 7);
      const newTo = addDays(prev.to, 7);
      fetchSchedule(newFrom, newTo);
      return { from: newFrom, to: newTo };
    });
  }, [fetchSchedule]);

  const previousWeek = useCallback(() => {
    setWeekRange((prev) => {
      const newFrom = addDays(prev.from, -7);
      const newTo = addDays(prev.to, -7);
      fetchSchedule(newFrom, newTo);
      return { from: newFrom, to: newTo };
    });
  }, [fetchSchedule]);

  const selectMobileDate = useCallback(
    (selectedDate: Date) => {
      setMobileRange({ from: selectedDate, to: selectedDate });
      fetchSchedule(selectedDate, selectedDate);
    },
    [fetchSchedule]
  );

  // Sterowanie zakresem dniowym (mobilnym)
  const nextDay = useCallback(() => {
    setMobileRange((prev) => {
      const newFrom = addDays(prev.from, 1);
      const newTo = addDays(prev.to, 1);
      fetchSchedule(newFrom, newTo);
      return { from: newFrom, to: newTo };
    });
  }, [fetchSchedule]);

  const previousDay = useCallback(() => {
    setMobileRange((prev) => {
      const newFrom = addDays(prev.from, -1);
      const newTo = addDays(prev.to, -1);
      fetchSchedule(newFrom, newTo);
      return { from: newFrom, to: newTo };
    });
  }, [fetchSchedule]);

  // Funkcja inicjująca pobranie harmonogramu – wywoływana w komponencie nadrzędnym
  const getFirstAllExercises = useCallback(() => {
    fetchSchedule(weekRange.from, weekRange.to);
  }, [fetchSchedule, weekRange]);

  // Dodajemy efekt reagujący na zmiany filtrów (i ewentualnie facilityIds oraz weekRange)
  useEffect(() => {
    if (facilityIds) {
      fetchSchedule(weekRange.from, weekRange.to);
    }
  }, [filters, facilityIds, weekRange.from, weekRange.to, fetchSchedule]);

  return {
    getFirstAllExercises,
    previousDay,
    nextDay,
    nextWeek,
    previousWeek,
    dayFromStateMobile: mobileRange.from,
    dayToStateMobile: mobileRange.to,
    dateFromState: weekRange.from,
    dateToState: weekRange.to,
    today,
    exercisesList,
    loading,
    refreshExercisesList: (isMobile?: boolean) =>
      fetchSchedule(isMobile ? mobileRange.from : weekRange.from, isMobile ? mobileRange.to : weekRange.to, true),
    filters,
    setFilters,
    selectMobileDate,
  };
}
